import React, { memo, useMemo } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "./contexts/ThemeContext";
import { SiteSettingsProvider } from "./contexts/SiteSettingsContext";
import { AuthProvider } from "./contexts/AuthContext";
import Header from "./components/Header";
import Footer from "./components/Footer";
import ScrollToTopComponent from "./components/ScrollToTop";
import InfoBar from "./components/InfoBar";
import ProtectedRoute from "./components/ProtectedRoute";
import { Toaster } from "./components/ui/toaster";
import { SiteContentProvider } from "./contexts/SiteContentContext";

// Lazy-loaded components
const Home = React.lazy(() => import("./pages/Home"));
const HostingPackages = React.lazy(() => import("./pages/HostingPackages"));
const Comparison = React.lazy(() => import("./pages/Comparison"));
const Dashboard = React.lazy(() => import("./pages/Dashboard"));
const AdminPanel = React.lazy(() => import("./pages/AdminPanel"));
const AdminSiteSettings = React.lazy(() => import("./pages/AdminSiteSettings"));
const AdminProducts = React.lazy(() => import("./pages/AdminProducts"));
const AdminCategories = React.lazy(() => import("./pages/AdminCategories"));
const AdminBlog = React.lazy(() => import("./pages/AdminBlog"));
const AdminNewsletter = React.lazy(() => import("./pages/AdminNewsletter"));
const AdminImages = React.lazy(() => import("./pages/AdminImages"));
const AdminTestimonials = React.lazy(() => import("./pages/AdminTestimonials"));
const AdminLogin = React.lazy(() => import("./pages/AdminLogin"));
const Minecraft = React.lazy(() => import("./pages/Minecraft"));
const ARK = React.lazy(() => import("./pages/ARK"));
const Rust = React.lazy(() => import("./pages/Rust"));
const Games = React.lazy(() => import("./pages/Games"));
const WebHosting = React.lazy(() => import("./pages/WebHosting"));
const Blog = React.lazy(() => import("./pages/Blog"));
const BlogDetail = React.lazy(() => import("./pages/BlogDetail"));
const CS2 = React.lazy(() => import("./pages/CS2"));
const Unturned = React.lazy(() => import("./pages/Unturned"));
const SCP = React.lazy(() => import("./pages/SCP"));
const MTA = React.lazy(() => import("./pages/MTA"));
const FiveM = React.lazy(() => import("./pages/FiveM"));
const Metin2 = React.lazy(() => import("./pages/Metin2"));
const Squad = React.lazy(() => import("./pages/Squad"));
const AssettoCorsa = React.lazy(() => import("./pages/AssettoCorsa"));
const GarrysMod = React.lazy(() => import("./pages/GarrysMod"));
const IntelServers = React.lazy(() => import("./pages/IntelServers"));
const Ryzen5950x = React.lazy(() => import("./pages/Ryzen5950x"));
const Ryzen7950x = React.lazy(() => import("./pages/Ryzen7950x"));
const NestedServer = React.lazy(() => import("./pages/NestedServer"));
const PhysicalServer = React.lazy(() => import("./pages/PhysicalServer"));
const ServerHosting = React.lazy(() => import("./pages/ServerHosting"));
const WordPressHosting = React.lazy(() => import("./pages/WordPressHosting"));
const ResellerHosting = React.lazy(() => import("./pages/ResellerHosting"));
const Contact = React.lazy(() => import("./pages/Contact"));
const About = React.lazy(() => import("./pages/About"));
const Kurumsal = React.lazy(() => import("./pages/Kurumsal"));
const AdminContent = React.lazy(() => import("./pages/AdminContent"));
const AdminLayout = React.lazy(() => import("./components/AdminLayout"));
const AcikRiza = React.lazy(() => import("./pages/AcikRiza"));
const HizmetSozlesmesi = React.lazy(() => import("./pages/HizmetSozlesmesi"));
const GizlilikPolitikasi = React.lazy(() => import("./pages/GizlilikPolitikasi"));
const KVKK = React.lazy(() => import("./pages/KVKK"));

// Loading spinner component
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen bg-slate-900">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
  </div>
);

function App() {
  return (
    <ThemeProvider>
      <SiteSettingsProvider>
        <SiteContentProvider>
          <AuthProvider>
            <div className="App">
              <BrowserRouter>
                <React.Suspense fallback={<LoadingSpinner />}>
                  <Routes>
                    {/* Routes without Header/Footer */}
                    <Route path="/dashboard" element={<Dashboard />} />

                    {/* Admin Login */}
                    <Route path="/kedihost/login" element={<AdminLogin />} />

                    {/* Admin Routes - Protected */}
                    <Route path="/kedihost/*" element={
                      <ProtectedRoute>
                        <Routes>
                          <Route element={<AdminLayout />}>
                            <Route index element={<AdminPanel />} />
                            <Route path="site-settings" element={<AdminSiteSettings />} />
                            <Route path="content" element={<AdminContent />} />
                            <Route path="products" element={<AdminProducts />} />
                            <Route path="categories" element={<AdminCategories />} />
                            <Route path="blog" element={<AdminBlog />} />
                            <Route path="newsletter" element={<AdminNewsletter />} />
                            <Route path="images" element={<AdminImages />} />
                            <Route path="yorumlar" element={<AdminTestimonials />} />
                          </Route>
                        </Routes>
                      </ProtectedRoute>
                    } />

                    {/* Routes with Header/Footer */}
                    <Route path="/*" element={
                      <>
                        <ScrollToTopComponent />
                        <InfoBar />
                        <Header />
                        <main id="main-content" className="flex-grow">
                          <Routes>
                            <Route path="/" element={<Home />} />
                            <Route path="/paketler" element={<HostingPackages />} />
                            <Route path="/paket-karsilastir" element={<Comparison />} />
                            <Route path="/minecraft" element={<Minecraft />} />
                            <Route path="/ark" element={<ARK />} />
                            <Route path="/rust" element={<Rust />} />
                            <Route path="/oyunlar" element={<Games />} />
                            <Route path="/web-hosting" element={<WebHosting />} />
                            <Route path="/blog" element={<Blog />} />
                            <Route path="/blog/:id" element={<BlogDetail />} />
                            <Route path="/cs2" element={<CS2 />} />
                            <Route path="/unturned" element={<Unturned />} />
                            <Route path="/scp" element={<SCP />} />
                            <Route path="/mta" element={<MTA />} />
                            <Route path="/fivem" element={<FiveM />} />
                            <Route path="/metin2" element={<Metin2 />} />
                            <Route path="/squad" element={<Squad />} />
                            <Route path="/assetto-corsa" element={<AssettoCorsa />} />
                            <Route path="/garrys-mod" element={<GarrysMod />} />
                            <Route path="/intel-sunucular" element={<IntelServers />} />
                            <Route path="/ryzen-5950x" element={<Ryzen5950x />} />
                            <Route path="/ryzen-7950x" element={<Ryzen7950x />} />
                            <Route path="/nested-sunucu" element={<NestedServer />} />
                            <Route path="/fiziksel-sunucu" element={<PhysicalServer />} />
                            <Route path="/sunucu-barindirma" element={<ServerHosting />} />
                            <Route path="/wordpress-hosting" element={<WordPressHosting />} />
                            <Route path="/reseller-hosting" element={<ResellerHosting />} />
                            <Route path="/iletisim" element={<Contact />} />
                            <Route path="/hakkimizda" element={<About />} />
                            <Route path="/kurumsal" element={<Kurumsal />} />
                            <Route path="/acik-riza-metni" element={<AcikRiza />} />
                            <Route path="/hizmet-sozlesmesi" element={<HizmetSozlesmesi />} />
                            <Route path="/gizlilik-politikasi" element={<GizlilikPolitikasi />} />
                            <Route path="/kvkk-metni" element={<KVKK />} />
                            {/* Büyük harfli URL'leri küçük harfli URL'lere yönlendir */}
                            <Route path="/Ryzen 5950x" element={<Navigate to="/ryzen-5950x" replace />} />
                            <Route path="/Ryzen 7950x" element={<Navigate to="/ryzen-7950x" replace />} />
                            <Route path="/5950x" element={<Navigate to="/ryzen-5950x" replace />} />
                            <Route path="/7950x" element={<Navigate to="/ryzen-7950x" replace />} />
                            <Route path="/Nested Sunucu" element={<Navigate to="/nested-sunucu" replace />} />
                            <Route path="/Fiziksel Sunucu" element={<Navigate to="/fiziksel-sunucu" replace />} />
                            <Route path="/Sunucu Barındırma" element={<Navigate to="/sunucu-barindirma" replace />} />
                            <Route path="/ARK" element={<Navigate to="/ark" replace />} />
                            <Route path="/Assetto Corsa" element={<Navigate to="/assetto-corsa" replace />} />
                            <Route path="/Counter-Strike 2" element={<Navigate to="/counter-strike-2" replace />} />
                            <Route path="/FiveM" element={<Navigate to="/fivem" replace />} />
                            <Route path="/Garry's Mod" element={<Navigate to="/garrys-mod" replace />} />
                            <Route path="/Intel" element={<Navigate to="/intel" replace />} />
                            <Route path="/MTA" element={<Navigate to="/mta" replace />} />
                            <Route path="/Metin2" element={<Navigate to="/metin2" replace />} />
                            <Route path="/Minecraft" element={<Navigate to="/minecraft" replace />} />
                            <Route path="/Nested" element={<Navigate to="/nested" replace />} />
                            <Route path="/Reseller Hosting" element={<Navigate to="/reseller-hosting" replace />} />
                            <Route path="/Rust" element={<Navigate to="/rust" replace />} />
                            <Route path="/SCP" element={<Navigate to="/scp" replace />} />
                            <Route path="/Squad" element={<Navigate to="/squad" replace />} />
                            <Route path="/Unturned" element={<Navigate to="/unturned" replace />} />
                            <Route path="/VPS" element={<Navigate to="/vps" replace />} />
                            <Route path="/Web Hosting" element={<Navigate to="/web-hosting" replace />} />
                            <Route path="/WordPress Hosting" element={<Navigate to="/wordpress-hosting" replace />} />
                            <Route path="/Cloud" element={<Navigate to="/cloud" replace />} />
                            <Route path="/Cloud Sunucu" element={<Navigate to="/cloud-sunucu" replace />} />
                            <Route path="/Paylaşımlı Hosting" element={<Navigate to="/paylasimli-hosting" replace />} />
                            <Route path="/SSL" element={<Navigate to="/ssl" replace />} />
                            <Route path="/VPS Sunucu" element={<Navigate to="/vps-sunucu" replace />} />

                            {/* Paket/Hosting Yönlendirmeleri */}
                            <Route path="/paket" element={<Navigate to="/paketler" replace />} />
                            <Route path="/packages" element={<Navigate to="/paketler" replace />} />
                            <Route path="/package" element={<Navigate to="/paketler" replace />} />
                            <Route path="/hosting" element={<Navigate to="/paketler" replace />} />
                            <Route path="/hosting-packages" element={<Navigate to="/paketler" replace />} />
                            <Route path="/wordpress" element={<Navigate to="/wordpress-hosting" replace />} />
                            <Route path="/reseller" element={<Navigate to="/reseller-hosting" replace />} />

                            {/* Oyun Yönlendirmeleri */}
                            <Route path="/games" element={<Navigate to="/oyunlar" replace />} />
                            <Route path="/oyun" element={<Navigate to="/oyunlar" replace />} />
                            <Route path="/game" element={<Navigate to="/oyunlar" replace />} />
                            <Route path="/gaming" element={<Navigate to="/oyunlar" replace />} />
                            <Route path="/game-servers" element={<Navigate to="/oyunlar" replace />} />
                            <Route path="/minecraft-server" element={<Navigate to="/minecraft" replace />} />
                            <Route path="/ark-server" element={<Navigate to="/ark" replace />} />
                            <Route path="/rust-server" element={<Navigate to="/rust" replace />} />
                            <Route path="/cs2-server" element={<Navigate to="/cs2" replace />} />
                            <Route path="/counter-strike" element={<Navigate to="/cs2" replace />} />
                            <Route path="/counter-strike-2" element={<Navigate to="/cs2" replace />} />

                            {/* Sunucu Yönlendirmeleri */}
                            <Route path="/server" element={<Navigate to="/intel-sunucular" replace />} />
                            <Route path="/servers" element={<Navigate to="/intel-sunucular" replace />} />
                            <Route path="/vps" element={<Navigate to="/intel-sunucular" replace />} />
                            <Route path="/vds" element={<Navigate to="/intel-sunucular" replace />} />
                            <Route path="/dedicated" element={<Navigate to="/fiziksel-sunucu" replace />} />
                            <Route path="/dedicated-server" element={<Navigate to="/fiziksel-sunucu" replace />} />
                            <Route path="/physical-server" element={<Navigate to="/fiziksel-sunucu" replace />} />
                            <Route path="/nested" element={<Navigate to="/nested-sunucu" replace />} />
                            <Route path="/cloud-server" element={<Navigate to="/nested-sunucu" replace />} />

                            {/* İletişim/Diğer Yönlendirmeleri */}
                            <Route path="/contact" element={<Navigate to="/iletisim" replace />} />
                            <Route path="/about" element={<Navigate to="/hakkimizda" replace />} />
                            <Route path="/company" element={<Navigate to="/hakkimizda" replace />} />
                            <Route path="/corporate" element={<Navigate to="/kurumsal" replace />} />
                            <Route path="/news" element={<Navigate to="/blog" replace />} />
                            <Route path="/articles" element={<Navigate to="/blog" replace />} />

                            {/* Admin Yönlendirmeleri - sadece /kedihost kullanılır, /admin yönlendirmesi yok */}
                            <Route path="/admin-panel" element={<Navigate to="/kedihost" replace />} />
                            <Route path="/management" element={<Navigate to="/kedihost" replace />} />

                            {/* Karşılaştırma Yönlendirmeleri */}
                            <Route path="/compare" element={<Navigate to="/paket-karsilastir" replace />} />
                            <Route path="/comparison" element={<Navigate to="/paket-karsilastir" replace />} />
                            <Route path="/karsilastirma" element={<Navigate to="/paket-karsilastir" replace />} />
                            {/* Bilinmeyen tüm URL'leri ana sayfaya yönlendir */}
                            <Route path="*" element={<Navigate to="/" replace />} />
                          </Routes>
                        </main>
                        <Footer />
                      </>
                    } />
                  </Routes>
                </React.Suspense>
                <Toaster />
              </BrowserRouter>
            </div>
          </AuthProvider>
        </SiteContentProvider>
      </SiteSettingsProvider>
    </ThemeProvider>
  );
}

export default App;


